import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Spinner,
    Flex,
    Button,
    Tag,
    Center,
    Select,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from "@chakra-ui/react";
import Pagination from "react-js-pagination";
import { fetchWrapper } from "../../helpers/fetch-wrapper";
import moment from "moment";
import Header from "../../components/Header";
import BoxWrapper from "../../components/BoxWrapper/BoxWrapper";

const VoucherCodes = () => {
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("all");
    const [selectedVoucher, setSelectedVoucher] = useState(null);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const loadList = async (pageNum = 1, currentStatus = status) => {
        try {
            setLoading(true);

            const baseUrl = `https://web-api.xnotify.com/api/voucher/list?page=${pageNum}&limit=10`;
            const url =
                currentStatus === "all" ? baseUrl : `${baseUrl}&status=${currentStatus}`;

            const data = await fetchWrapper.get(url);

            if (data?.success) {
                setList(data.data.voucher_codes || []);
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error("Error fetching voucher codes:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadList(page, status);
    }, [page, status]);

    const getStatusColor = (status) => {
        switch (status) {
            case "used":
                return "green";
            case "unused":
                return "yellow";
            case "expired":
                return "red";
            default:
                return "gray";
        }
    };

    return (
        <>
            <Header />
            <BoxWrapper>
                {/* Header + Filter */}
                <Flex justify="space-between" align="center" mb={4}>
                    <Flex direction="column">
                        <Text fontSize="xl" fontWeight="bold" color="gray.700">
                            Voucher Codes
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            Total: {pagination?.total || 0}
                        </Text>
                    </Flex>

                    <Select
                        w="200px"
                        size="sm"
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="all">All</option>
                        <option value="used">Used</option>
                        <option value="unused">Unused</option>
                    </Select>
                </Flex>

                {loading ? (
                    <Center py={20}>
                        <Spinner size="xl" />
                    </Center>
                ) : (
                    <Table variant="simple" colorScheme="gray" size="md">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th>ID</Th>
                                <Th>Voucher Code</Th>
                                <Th>Status</Th>
                                <Th>Full Name</Th>
                                <Th>Company</Th>
                                <Th>Email</Th>
                                <Th>Phone</Th>
                                <Th>Used At</Th>
                                <Th>Created At</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {list.length > 0 ? (
                                list.map((vc, index) => (
                                    <Tr
                                        key={vc.id}
                                        _hover={{ bg: "gray.50" }}
                                        bg={index % 2 === 0 ? "white" : "gray.50"}
                                    >
                                        <Td>{vc.id}</Td>
                                        <Td
                                            fontWeight="600"
                                            color="blue.600"
                                            cursor="pointer"
                                            onClick={() => {
                                                setSelectedVoucher(vc);
                                                onOpen();
                                            }}
                                        >
                                            {vc.voucher_code}
                                        </Td>
                                        <Td>
                                            <Tag
                                                colorScheme={getStatusColor(vc.status)}
                                                px={3}
                                                py={1}
                                                borderRadius="md"
                                            >
                                                {vc.status}
                                            </Tag>
                                        </Td>
                                        <Td>{vc.full_name || "-"}</Td>
                                        <Td>{vc.company_name || "-"}</Td>
                                        <Td>{vc.email_address || "-"}</Td>
                                        <Td>{vc.phone_number || "-"}</Td>
                                        <Td>
                                            {vc.used_at
                                                ? moment(vc.used_at).format("DD MMM YYYY")
                                                : "-"}
                                        </Td>
                                        <Td>{moment(vc.created_at).format("DD MMM YYYY")}</Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={9} textAlign="center" py={10}>
                                        No voucher codes found.
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                )}

                {/* ✅ React-js-pagination Component */}
                {!loading && pagination.pages > 1 && (
                    <Flex justify="center" mt={6}>
                        <Pagination
                            activePage={page}
                            itemsCountPerPage={10}
                            totalItemsCount={pagination.total || 0}
                            pageRangeDisplayed={3}
                            onChange={(pageNum) => setPage(pageNum)}
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination"
                        />
                    </Flex>
                )}
            </BoxWrapper>

            {/* ✅ Details Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Voucher Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {selectedVoucher ? (
                            <Box>
                                <Text><b>Voucher Code:</b> {selectedVoucher.voucher_code}</Text>
                                <Text><b>Status:</b> {selectedVoucher.status}</Text>
                                <Text><b>Full Name:</b> {selectedVoucher.full_name || "-"}</Text>
                                <Text><b>Company:</b> {selectedVoucher.company_name || "-"}</Text>
                                <Text><b>Email:</b> {selectedVoucher.email_address || "-"}</Text>
                                <Text><b>Phone:</b> {selectedVoucher.phone_number || "-"}</Text>
                                <Text><b>Used At:</b> {selectedVoucher.used_at ? moment(selectedVoucher.used_at).format("DD MMM YYYY, h:mm A") : "-"}</Text>
                                <Text><b>Created At:</b> {moment(selectedVoucher.created_at).format("DD MMM YYYY, h:mm A")}</Text>
                            </Box>
                        ) : (
                            <Text>No voucher selected.</Text>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default VoucherCodes;
